import { useState } from "react";

export type DonutSegment = {
  id: string;
  share: number;
  color: string;
  title: string;
  percent: string;
  detail?: string;
};

const width = 520;
const height = 300;
const cx = width / 2;
const cy = height / 2;
const radius = 74;
const stroke = 26;
const labelColumn = 156;
const circumference = 2 * Math.PI * radius;

type Point = { x: number; y: number };

type PlacedLabel = DonutSegment & {
  start: Point;
  elbow: Point;
  end: Point;
  right: boolean;
};

const polar = (angle: number, distance: number): Point => ({
  x: cx + distance * Math.sin(angle),
  y: cy - distance * Math.cos(angle),
});

const blockHeight = (title: string) => Math.max(1, Math.ceil(title.length / 18)) * 16 + 20;

const arcPath = (start: number, end: number) => {
  const from = polar(start, radius);
  const to = polar(end, radius);
  const large = end - start > Math.PI ? 1 : 0;
  return `M ${from.x} ${from.y} A ${radius} ${radius} 0 ${large} 1 ${to.x} ${to.y}`;
};

function placeLabels(segments: readonly DonutSegment[]): PlacedLabel[] {
  let cursor = 0;
  const placed = segments.map((segment) => {
    const angle = ((cursor + segment.share / 2) / 100) * 2 * Math.PI;
    cursor += segment.share;
    const right = Math.sin(angle) >= 0;
    return {
      ...segment,
      start: polar(angle, radius + stroke / 2 + 4),
      elbow: polar(angle, radius + stroke / 2 + 22),
      end: { x: 0, y: 0 },
      right,
    };
  });
  for (const side of [true, false]) {
    const column = placed
      .filter((label) => label.right === side)
      .sort((left, right) => left.elbow.y - right.elbow.y);
    column.forEach((label, index) => {
      const previous = column[index - 1];
      if (!previous) return;
      const min = previous.elbow.y + (blockHeight(previous.title) + blockHeight(label.title)) / 2 + 10;
      if (label.elbow.y < min) label.elbow.y = min;
    });
    for (const label of column) label.elbow.y = Math.min(height - 16, Math.max(16, label.elbow.y));
  }
  return placed.map((label) => ({
    ...label,
    end: {
      x: label.right
        ? Math.min(label.elbow.x + 18, width - labelColumn)
        : Math.max(label.elbow.x - 18, labelColumn),
      y: label.elbow.y,
    },
  }));
}

export function DomainDonut({
  segments,
  centerValue,
  centerLabel,
  description,
}: {
  segments: readonly DonutSegment[];
  centerValue: string;
  centerLabel: string;
  description: string;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const labels = placeLabels(segments);
  const gap = segments.length > 1 ? 3 : 0;
  const gapAngle = gap / radius;
  let shareCursor = 0;
  const arcs = labels.map((label) => {
    const start = (shareCursor / 100) * Math.PI * 2 + gapAngle / 2;
    const end = ((shareCursor + label.share) / 100) * Math.PI * 2 - gapAngle / 2;
    shareCursor += label.share;
    return { ...label, path: arcPath(start, end), hover: polar((start + end) / 2, radius + stroke / 2 + 6) };
  });
  let offset = 0;

  return (
    <div className="relative">
      <p className="sr-only">{description}</p>
      <svg className="h-auto w-full" viewBox={`0 0 ${width} ${height}`}>
        <g aria-hidden="true" pointerEvents="none">
          <circle cx={cx} cy={cy} fill="none" r={radius} stroke="var(--muted)" strokeWidth={stroke} />
          {segments.map((segment) => {
            const length = (segment.share / 100) * circumference;
            const dash = Math.max(0, length - gap);
            const circle = (
              <circle
                cx={cx}
                cy={cy}
                fill="none"
                key={segment.id}
                r={radius}
                stroke={segment.color}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset - gap / 2}
                strokeLinecap="butt"
                strokeWidth={activeId === segment.id ? stroke + 6 : stroke}
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
            offset += length;
            return circle;
          })}
          {labels.map((label) => (
            <polyline
              fill="none"
              key={label.id}
              points={`${label.start.x},${label.start.y} ${label.elbow.x},${label.elbow.y} ${label.end.x},${label.end.y}`}
              stroke="var(--muted-foreground)"
              strokeOpacity={0.4}
              strokeWidth={1}
            />
          ))}
          <text
            dominantBaseline="middle"
            fill="var(--foreground)"
            fontSize={28}
            fontWeight={500}
            textAnchor="middle"
            x={cx}
            y={cy - 8}
          >
            {centerValue}
          </text>
          <text
            dominantBaseline="middle"
            fill="var(--muted-foreground)"
            fontSize={12}
            textAnchor="middle"
            x={cx}
            y={cy + 16}
          >
            {centerLabel}
          </text>
        </g>
        {arcs.map((arc) => (
          <path
            aria-label={`${arc.title}, ${arc.percent}${arc.detail ? `. ${arc.detail}` : ""}`}
            className="cursor-pointer outline-none"
            d={arc.path}
            data-control-id={`manager.dashboard.domain.${arc.id}`}
            fill="none"
            key={arc.id}
            onBlur={() => setActiveId((current) => (current === arc.id ? null : current))}
            onFocus={() => setActiveId(arc.id)}
            onMouseEnter={() => setActiveId(arc.id)}
            onMouseLeave={() => setActiveId((current) => (current === arc.id ? null : current))}
            pointerEvents="stroke"
            stroke="transparent"
            strokeWidth={stroke + 12}
            tabIndex={0}
          />
        ))}
      </svg>
      {arcs.map((label) => {
        const edge = ((label.end.x + (label.right ? 8 : -8)) / width) * 100;
        return (
          <p
            className={`absolute -translate-y-1/2 text-xs leading-snug break-words ${label.right ? "text-left" : "text-right"}`}
            key={label.id}
            style={{
              top: `${(label.end.y / height) * 100}%`,
              left: label.right ? `${edge}%` : "1.5%",
              width: label.right ? `${98 - edge}%` : `${edge - 1.5}%`,
            }}
          >
            <span className="block text-muted-foreground">{label.title}</span>
            <span className="font-medium text-foreground tabular-nums">{label.percent}</span>
          </p>
        );
      })}
      {arcs.map((arc) =>
        activeId === arc.id && arc.detail ? (
          <span
            className={`pointer-events-none absolute z-10 w-max max-w-48 rounded-xl border border-border/60 bg-background px-3 py-2 text-left shadow-md ${arc.right ? "translate-x-2 -translate-y-1/2" : "-translate-x-[calc(100%+0.5rem)] -translate-y-1/2"}`}
            key={`${arc.id}-hover`}
            role="tooltip"
            style={{ left: `${(arc.hover.x / width) * 100}%`, top: `${(arc.hover.y / height) * 100}%` }}
          >
            <span className="block text-xs text-muted-foreground">{arc.title}</span>
            <span className="mt-0.5 block text-sm font-medium">{arc.detail}</span>
          </span>
        ) : null,
      )}
    </div>
  );
}
